import React, { useState } from 'react'
import { Box, Divider, Typography } from '@mui/material'
import { NAVY, BORDER, SUBTLE, WHITE, withOpacity } from '../styles/foundation/colors'
import { FONT, SIZE, WEIGHT } from '../styles/foundation/typography'

// One level of tab nesting, drawn as a "tray" that visually branches (via a caret + stem)
// from the active parent tab of an outer tab bar. Used to make it obvious that a set of
// screens live INSIDE a parent tab -- e.g. Manifest -> [Pending Pickups, ...] or
// Label -> [By Pincode & Date, By AWB Upload].
//
// caretX: the active parent tab's centre in px, measured by the parent (tabs are unequal
//   width, so a fixed % won't do). Falls back to `fallbackCaret` until measured.
// tabs: [{ value, label, icon, render: () => <Content/> }]
export default function NestedTabTray({
    caretX = null,
    fallbackCaret = '85%',
    color = '#7c4dff',
    parentLabel,
    parentIcon,
    tabs = [],
    defaultValue,
}) {
    const [value, setValue] = useState(defaultValue ?? tabs[0]?.value)
    const active = tabs.find((t) => t.value === value) || tabs[0]
    if (!active) return null

    const caretPos = caretX != null ? `${caretX}px` : fallbackCaret
    const trayBg = withOpacity(color, 0.06)
    const trayBorder = withOpacity(color, 0.28)

    return (
        <Box>
            {/* Tray: breadcrumb + segmented sub-tabs, tinted with the parent tab's colour.
                A single clean caret on its top edge points up to the active parent tab. */}
            <Box sx={{ position: 'relative', mt: '11px', bgcolor: trayBg, border: `1px solid ${trayBorder}`, borderRadius: '12px', p: '12px 14px', mb: 2 }}>
                {/* Up-caret = a border-coloured triangle with a tray-coloured triangle 1px in front,
                    so it reads as a notch in the tray's top edge (no diamond / no line-through). */}
                <Box sx={{ position: 'absolute', top: '-9px', left: caretPos, transform: 'translateX(-50%)', width: 0, height: 0, borderLeft: '9px solid transparent', borderRight: '9px solid transparent', borderBottom: `9px solid ${trayBorder}` }} />
                <Box sx={{ position: 'absolute', top: '-8px', left: caretPos, transform: 'translateX(-50%)', width: 0, height: 0, borderLeft: '8px solid transparent', borderRight: '8px solid transparent', borderBottom: `8px solid ${trayBg}` }} />

                {/* Breadcrumb: <parent> › <active sub-tab> */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.25, flexWrap: 'wrap' }}>
                    {parentLabel && (
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, bgcolor: WHITE, border: `1px solid ${withOpacity(color, 0.3)}`, color, fontFamily: FONT, fontWeight: WEIGHT.semibold, borderRadius: '6px', px: 0.9, py: 0.3, fontSize: 11, '& .MuiSvgIcon-root': { fontSize: 13 } }}>
                            {parentIcon}
                            {parentLabel}
                        </Box>
                    )}
                    {parentLabel && <Typography sx={{ fontFamily: FONT, fontSize: 11, color: SUBTLE }}>›</Typography>}
                    <Typography sx={{ fontFamily: FONT, fontSize: 11, color: NAVY, fontWeight: WEIGHT.semibold }}>{active.label}</Typography>
                </Box>

                {/* Segmented sub-tab control — dividers keep the three reading as distinct tabs */}
                <Box sx={{ display: 'inline-flex', alignItems: 'center', bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: '10px', p: '4px' }}>
                    {tabs.map((t, i) => {
                        const on = t.value === value
                        const prevOn = i > 0 && tabs[i - 1].value === value
                        return (
                            <React.Fragment key={t.value}>
                                {/* hide the divider that would sit against an active (filled) pill */}
                                {i > 0 && (
                                    <Divider orientation="vertical" flexItem sx={{ borderColor: (on || prevOn) ? 'transparent' : BORDER, mx: 0.5, my: 0.75 }} />
                                )}
                                <Box
                                    onClick={() => setValue(t.value)}
                                    sx={{
                                        display: 'flex', alignItems: 'center', gap: 0.75, px: 1.75, py: 0.9, borderRadius: '7px', cursor: 'pointer',
                                        bgcolor: on ? color : 'transparent', color: on ? WHITE : SUBTLE,
                                        fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: on ? WEIGHT.semibold : WEIGHT.medium,
                                        transition: 'all 0.15s',
                                        // icon follows the text colour: white on the active pill, subtle grey otherwise
                                        '& .MuiSvgIcon-root': { fontSize: 15 },
                                        '&:hover': { bgcolor: on ? color : withOpacity(color, 0.08) },
                                    }}
                                >
                                    {t.icon}
                                    <span style={{ whiteSpace: 'nowrap' }}>{t.label}</span>
                                </Box>
                            </React.Fragment>
                        )
                    })}
                </Box>
            </Box>

            {/* Active sub-screen content, below the tray */}
            {active.render ? active.render() : null}
        </Box>
    )
}
